import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { SessionInfo, Settings, User, WeekStart } from "@if-tracking/shared";

import { api, ApiError } from "../api";
import { formatDate, formatTime } from "../format";

interface SettingsPanelProps {
  settings: Settings;
  user: User;
  onSaved: (updated: Settings) => void;
  onSignOut: () => void | Promise<void>;
  notify: (message: string, kind?: "info" | "success" | "error") => void;
}

export function SettingsPanel({ settings, user, onSaved, onSignOut, notify }: SettingsPanelProps) {
  const [targetHours, setTargetHours] = useState((settings.default_target_seconds / 3600).toString());
  const [timezone, setTimezone] = useState(settings.timezone);
  const [weekStart, setWeekStart] = useState<WeekStart>(settings.week_start);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const hours = Number(targetHours);
    if (!Number.isFinite(hours) || hours <= 0) {
      notify("Default target must be a positive number of hours.", "error");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.updateSettings({
        default_target_seconds: Math.round(hours * 3600),
        timezone: timezone.trim(),
        week_start: weekStart
      });
      onSaved(updated);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Save failed", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h1>Settings</h1>
      </div>

      <form className="card stack" onSubmit={handleSubmit}>
        <label>
          Default target (hours)
          <input
            type="number"
            min="0.25"
            step="0.25"
            value={targetHours}
            onChange={(e) => setTargetHours(e.target.value)}
            required
          />
        </label>
        <label>
          Timezone (IANA)
          <input type="text" value={timezone} onChange={(e) => setTimezone(e.target.value)} required />
          <span className="muted small">Dates and times are interpreted in this zone.</span>
        </label>
        <label>
          Week starts on
          <select value={weekStart} onChange={(e) => setWeekStart(e.target.value as WeekStart)}>
            <option value="mon">Monday</option>
            <option value="sun">Sunday</option>
          </select>
        </label>
        <div className="row">
          <button type="submit" className="primary" disabled={busy}>
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>

      <AccountCard user={user} timezone={settings.timezone} onSignOut={onSignOut} notify={notify} />
    </section>
  );
}

interface AccountCardProps {
  user: User;
  timezone: string;
  onSignOut: () => void | Promise<void>;
  notify: (message: string, kind?: "info" | "success" | "error") => void;
}

function AccountCard({ user, timezone, onSignOut, notify }: AccountCardProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);

  const loadSessions = useCallback(async () => {
    try {
      setSessions(await api.listSessions());
    } catch (error) {
      if (!(error instanceof ApiError && error.isAuth)) {
        notify(error instanceof Error ? error.message : "Failed to load sessions", "error");
      }
    }
  }, [notify]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  async function handlePasswordChange(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < 8) {
      notify("New password must be at least 8 characters.", "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      notify("New passwords do not match.", "error");
      return;
    }

    setBusy(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      notify("Password changed. Other devices must sign in again.", "success");
      await loadSessions();
    } catch (error) {
      if (error instanceof ApiError && error.isAuth) {
        notify("Current password is incorrect.", "error");
      } else {
        notify(error instanceof Error ? error.message : "Password change failed", "error");
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(session: SessionInfo) {
    const label = describeSession(session);
    if (!window.confirm(`Sign ${label} out? It will need to sign in again.`)) return;
    try {
      await api.revokeSession(session.id);
      notify(`${label} signed out`, "success");
      await loadSessions();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not revoke", "error");
    }
  }

  return (
    <>
      <form className="card stack" onSubmit={handlePasswordChange}>
        <div className="row space-between">
          <span className="section-label">Account</span>
          <button type="button" className="link-button" onClick={() => void onSignOut()}>
            Sign out
          </button>
        </div>
        <p className="muted small">
          Signed in as <strong>{user.username}</strong>. Your fasts, settings, and exports are yours alone.
        </p>

        <label>
          Current password
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </label>
        <label>
          New password
          <input
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <span className="muted small">At least 8 characters.</span>
        </label>
        <label>
          Confirm new password
          <input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </label>
        <div className="row">
          <button type="submit" className="primary" disabled={busy}>
            {busy ? "Changing…" : "Change password"}
          </button>
        </div>
      </form>

      <div className="card stack">
        <span className="section-label">Signed-in devices</span>
        <p className="muted small">
          Sign in on the iPhone or Watch app with the same username and password. Revoke a device here if you
          lose it.
        </p>
        {sessions === null ? (
          <div className="empty">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="empty">No active sessions.</div>
        ) : (
          <ul className="session-list">
            {sessions.map((session) => (
              <li key={session.id}>
                <div>
                  <strong>{describeSession(session)}</strong>
                  {session.current && <span className="badge">this one</span>}
                  <span className="muted small">
                    {" "}
                    last used {formatDate(session.last_used_at, timezone)} at{" "}
                    {formatTime(session.last_used_at, timezone)}
                  </span>
                </div>
                {!session.current && (
                  <button type="button" className="link-button danger" onClick={() => void handleRevoke(session)}>
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function describeSession(session: SessionInfo): string {
  if (session.label) return session.label;
  return session.kind === "device" ? "Device" : "Browser";
}
